import { useFormik } from "formik";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { toast } from "@outpave/ds";
import { useEffect, useState } from "react";
import { generateInvoiceValidation } from "../validations/sendInvoice";
import {
  CANCEL_INVOICE,
  CREATE_INVOICE,
  DOES_INVOICE_NUMBER_EXIST,
  GET_INVOICE,
  GET_MOST_RECENT_INVOICE_NUMBER,
  REFUND_INVOICE,
  RESEND_FAILED_PAYMENT_NOTICE,
  SEND_INVOICE,
  UNCANCEL_INVOICE,
  UPDATE_INVOICE,
  UPDATE_INVOICE_DETAILS,
} from "../graphql/invoice";
import { InvoicePaymentTerms } from "../enums/InvoicePaymentTerms";
import { InvoiceStatus } from "../enums/InvoiceStatus";
import { InvoiceDiscountType } from "../enums/InvoiceDiscountType";
import { useNavigate } from "react-router-dom";
import { useAttachmentAndAssetHandling } from "./useAttachmentAndAssetHandling";
import { ATTACHMENT_PARENT_TYPE } from "enums/AttachmentParentType";
import { useTranslation } from "react-i18next";
import { GET_SERVICE_FEES } from "graphql/configuration";
import { isEqual } from "lodash";
import { GET_INVOICE_NUMBER_SETTINGS } from "graphql/company";
import ContactType from "enums/ContactType";
import {
  calculateInvoiceDerivedFields,
  FormikInvoice,
  InvoiceDetail,
  formatInvoiceInputForCreateEdit,
  invoiceInitialValues,
  INVOICE_CURRENCY,
} from "utils/invoices/invoiceFormat";
import useCustomFieldRenderer from "hooks/useCustomFieldRenderer";
import CustomFieldParentType from "enums/CustomFieldParentType";
import { fullName } from "utils/fullName";

interface UseInvoiceDataProps {
  invoiceId?: string;
  shouldFetchAttachments?: boolean;
  shouldRefetchAttachments?: boolean;
  shouldNotQueryInvoice?: boolean;
}

export function useInvoiceData({
  invoiceId,
  shouldFetchAttachments,
  shouldRefetchAttachments,
  shouldNotQueryInvoice,
}: UseInvoiceDataProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedContactId, setSelectedContactId] = useState<string>("");

  const [
    getInvoice,
    { data: invoiceData, loading: populatingInvoice, refetch: refetchInvoice },
  ] = useLazyQuery(GET_INVOICE, {
    fetchPolicy: "cache-and-network",
  });

  const [
    getMostRecentInvoiceNumber,
    { data: recentInvoiceNumber, loading: loadingInvoiceNumber },
  ] = useLazyQuery(GET_MOST_RECENT_INVOICE_NUMBER, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (!invoiceId && recentInvoiceNumber?.mostRecentInvoiceNumber) {
      formikInvoice.setFieldValue(
        "invoiceNumber",
        recentInvoiceNumber?.mostRecentInvoiceNumber,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentInvoiceNumber, invoiceId]);

  const { data: invoiceSettings } = useQuery(GET_INVOICE_NUMBER_SETTINGS, {
    fetchPolicy: "network-only",
  });

  const defaultPaymentTerms =
    invoiceSettings?.getInvoiceNumberSettings?.defaultPaymentTerms ||
    InvoicePaymentTerms.IMMEDIATELY;
  const defaultTax = invoiceSettings?.getInvoiceNumberSettings?.defaultTax || 0;
  const defaultTaxType =
    invoiceSettings?.getInvoiceNumberSettings?.defaultTaxType ||
    InvoiceDiscountType.PERCENTAGE;

  const {
    attachments,
    handleAddAttachments,
    handleUpdateAttachments,
    refetchAttachments,
  } = useAttachmentAndAssetHandling({
    parentDetails: {
      parentId: invoiceId || "",
      parentType: ATTACHMENT_PARENT_TYPE.INVOICE,
    },
    shouldFetchAttachments,
    shouldRefetchAttachments,
  });

  const [createInvoice, { loading: creatingInvoice }] =
    useMutation(CREATE_INVOICE);
  const [updateInvoice, { loading: updatingInvoice }] =
    useMutation(UPDATE_INVOICE);
  const [cancelInvoice, { loading: cancelingInvoice }] =
    useMutation(CANCEL_INVOICE);
  const [uncancelInvoice, { loading: uncancelingInvoice }] =
    useMutation(UNCANCEL_INVOICE);

  const [updateInvoiceDetails, { loading: updatingInvoiceDetail }] =
    useMutation(UPDATE_INVOICE_DETAILS);

  const [sendInvoice, { loading: sendingInvoice }] = useMutation(SEND_INVOICE);
  const [resendFailedPaymentNotice, { loading: resendingFailedNotice }] =
    useMutation(RESEND_FAILED_PAYMENT_NOTICE);
  const [doesInvoiceNumberExist] = useLazyQuery(DOES_INVOICE_NUMBER_EXIST);

  const [refundInvoice, { loading: refundingInvoice }] =
    useMutation(REFUND_INVOICE);

  const normalizeContact = (contact: any) =>
    contact
      ? {
          id: contact.id,
          firstName: contact.firstName,
          lastName: contact.lastName,
          businessName: contact.businessName,
          email: contact.email,
          contactType: contact.contactType,
          phone: contact.phone,
          address: contact.address,
        }
      : null;

  const {
    customFieldSettings,
    customFieldsInitialValues,
    transformCustomFieldsToArray,
    transformCustomFieldsToRecord,
  } = useCustomFieldRenderer({
    parentType: CustomFieldParentType.INVOICE,
    inheritFrom:
      !invoiceId && selectedContactId
        ? {
            type: CustomFieldParentType.CONTACT,
            id: selectedContactId,
          }
        : undefined,
  });

  const validationSchema = generateInvoiceValidation(customFieldSettings);

  const formikInvoice = useFormik<FormikInvoice>({
    initialValues: invoiceInitialValues,
    validationSchema: validationSchema,
    onSubmit: async () => {},
  });

  useEffect(() => {
    if (invoiceId && !shouldNotQueryInvoice) {
      getInvoice({ variables: { id: invoiceId } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  useEffect(() => {
    const contactId = formikInvoice.values?.primaryRecipient?.id;
    if (contactId) {
      setSelectedContactId(contactId);
    }
  }, [formikInvoice.values?.primaryRecipient?.id]);

  // if we have data on the invoice, we populate the form with that data else reset the form to initial values
  useEffect(() => {
    if (invoiceData && invoiceData.invoice && invoiceId) {
      const invoice = invoiceData.invoice;

      const invoicePaidByName =
        invoice?.paidBy?.contactType === ContactType.Business
          ? invoice?.paidBy?.businessName
          : fullName(invoice?.paidBy);

      const newInitialValues = {
        id: invoice.id,
        invoiceNumber: invoice.invoiceNumber,
        invoiceName: invoice.invoiceName,
        purchaseOrderId: invoice.purchaseOrderId || "",
        primaryRecipient: normalizeContact(invoice.primaryRecipient),
        secondaryRecipients: invoice.secondaryRecipients || null,
        paymentTerms: invoice.paymentTerms,
        invoiceDate: new Date(invoice.invoiceDate),
        sentDate: invoice.sentDate ? new Date(invoice.sentDate) : null,
        dueDate: invoice.dueDate ? new Date(invoice.dueDate) : null,
        paidDate: invoice.paidDate ? new Date(invoice.paidDate) : null,
        paidBy: invoice.paidBy
          ? {
              id: invoice.paidBy.id,
              name: invoicePaidByName,
            }
          : null,
        subtotal: invoice.subtotal,
        discount: invoice.discount,
        discountType: invoice.discountType || InvoiceDiscountType.AMOUNT,
        discountAmount: invoice.discountAmount,
        serviceFee: invoice.serviceFee,
        serviceFeeType: invoice.serviceFeeType || InvoiceDiscountType.AMOUNT,
        serviceFeeAmount: invoice.serviceFeeAmount,
        tax: invoice.tax,
        taxType: invoice.taxType || InvoiceDiscountType.AMOUNT,
        taxAmount: invoice.taxAmount,
        total: invoice.total,
        currency: INVOICE_CURRENCY,
        submittedBy: invoice.submittedBy,
        sentBy: invoice.sentBy,
        attachments: attachments || [],
        details: invoice.details
          ? invoice.details.map((detail: InvoiceDetail) => ({
              id: detail.id,
              invoiceId: detail.invoiceId,
              order: detail.order,
              productOrService: detail.productOrService,
              description: detail.description,
              amount: detail.amount,
              total: detail.total,
              quantity: detail.quantity,
            }))
          : [],
        status: invoice.status,
        createdAt: new Date(invoice.createdAt),
        note: invoice.note,
        allocationType: invoice.allocationType || null,
        allocationName: invoice.allocationName || null,
        transactionId: invoice.transactionId || null,
        companyDetails: invoice.companyDetails || null,
        sentHistory: invoice.sentHistory || [],
        estimateActionHistory: invoice.estimateActionHistory || [],
        disputeData: invoice.disputeData || null,
        linkedTransaction: invoice.transaction || [],
        canceledBy: invoice.canceledBy || null,
        canceledDate: invoice.canceledDate
          ? new Date(invoice.canceledDate)
          : null,
        customFields: transformCustomFieldsToRecord(invoice.customFields),
      };
      formikInvoice.resetForm({ values: newInitialValues });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, invoiceData?.invoice]);

  useEffect(() => {
    if (invoiceId) return; // Skip if editing

    formikInvoice.setFieldValue("paymentTerms", defaultPaymentTerms);
    formikInvoice.setFieldValue("tax", defaultTax);
    formikInvoice.setFieldValue("taxType", defaultTaxType);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPaymentTerms, defaultTax, defaultTaxType, invoiceId]);

  const { data: serviceFeeConfig } = useQuery(GET_SERVICE_FEES, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (invoiceId) return; // Skip if editing

    const currentCustomFields = formikInvoice.values.customFields;
    if (!isEqual(currentCustomFields, customFieldsInitialValues)) {
      formikInvoice.setFieldValue("customFields", customFieldsInitialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFieldsInitialValues]);

  useEffect(() => {
    formikInvoice.resetForm({
      values: { ...formikInvoice.values, attachments: attachments },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(attachments)]);

  const handleSaveInvoice = async (
    saveAsDraft: boolean = false,
    updateCustomFieldsOnly: boolean = false,
  ): Promise<string | undefined> => {
    let invoiceId = formikInvoice.values.id;
    let status = saveAsDraft ? InvoiceStatus.DRAFT : InvoiceStatus.PENDING;

    try {
      if (updateCustomFieldsOnly && invoiceId) {
        await handleUpdateInvoiceCustomFields(invoiceId, formikInvoice.values);
        return invoiceId;
      }

      if (invoiceId) {
        await handleUpdateInvoice({ ...formikInvoice.values, status });
        formikInvoice.resetForm({
          values: { ...formikInvoice.values, status },
        });
        refetchAttachments();
        return invoiceId;
      } else {
        const createdInvoiceId = await handleCreateInvoice({
          ...formikInvoice.values,
          status,
        });
        formikInvoice.setFieldValue("status", status);
        handleOpenEditInvoicePage(createdInvoiceId);
        return createdInvoiceId;
      }
    } catch (error) {
      console.error("Error saving invoice:", error);
      return undefined;
    }
  };

  useEffect(() => {
    if (!formikInvoice.dirty) return;

    const derived = calculateInvoiceDerivedFields(
      formikInvoice?.values,
      serviceFeeConfig?.getServiceFeeConfig,
    );

    const newValues = {
      ...formikInvoice.values,
      ...derived,
    };

    if (
      newValues.subtotal !== formikInvoice.values.subtotal ||
      newValues.discountAmount !== formikInvoice.values.discountAmount ||
      newValues.serviceFeeAmount !== formikInvoice.values.serviceFeeAmount ||
      newValues.taxAmount !== formikInvoice.values.taxAmount ||
      newValues.total !== formikInvoice.values.total ||
      !isEqual(newValues.details, formikInvoice.values.details)
    ) {
      formikInvoice.setValues(newValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formikInvoice.values.details,
    formikInvoice.values.discount,
    formikInvoice.values.discountType,
    formikInvoice.values.serviceFee,
    formikInvoice.values.serviceFeeType,
    formikInvoice.values.tax,
    formikInvoice.values.taxType,
    serviceFeeConfig,
  ]);

  async function handleCreateInvoice(invoice: FormikInvoice): Promise<string> {
    try {
      const createdInvoice = await createInvoice({
        variables: {
          input: formatInvoiceInputForCreateEdit(
            invoice,
            transformCustomFieldsToArray,
          ),
        },
      });

      // create the invoice details
      // Bulk upload all invoice details
      await updateInvoiceDetails({
        variables: {
          input: {
            invoiceId: createdInvoice.data.createInvoice.id,
            details: invoice.details.map((detail) => ({
              id: detail.id,
              order: detail.order,
              productOrService: detail.productOrService,
              description: detail.description,
              amount: Number(detail.amount),
              quantity: detail.quantity,
              total: Number(detail.total),
            })),
          },
        },
      });

      await handleAddAttachments(formikInvoice.values?.attachments, {
        parentId: createdInvoice.data.createInvoice.id,
        parentType: ATTACHMENT_PARENT_TYPE.INVOICE,
      });

      toast.success(t("Invoice created successfully"));
      return createdInvoice.data.createInvoice.id;
    } catch (error: any) {
      toast.error(t("Error creating invoice") + ": " + error.message);
      throw error;
    }
  }

  async function handleUpdateInvoice(invoice: FormikInvoice): Promise<string> {
    try {
      if (!invoice.id) return "";
      const updatedInvoice = await updateInvoice({
        variables: {
          id: invoice.id,
          input: formatInvoiceInputForCreateEdit(
            invoice,
            transformCustomFieldsToArray,
          ),
        },
      });

      // Update all invoice details in one call
      await updateInvoiceDetails({
        variables: {
          input: {
            invoiceId: invoice.id,
            details: invoice.details.map((detail) => ({
              id: detail.id,
              order: detail.order,
              productOrService: detail.productOrService,
              description: detail.description,
              amount: Number(detail.amount),
              quantity: detail.quantity,
              total: Number(detail.total),
            })),
          },
        },
      });

      await handleUpdateAttachments(formikInvoice.values?.attachments, {
        parentId: updatedInvoice.data.updateInvoice.id,
        parentType: ATTACHMENT_PARENT_TYPE.INVOICE,
      });

      await handleAddAttachments(formikInvoice.values?.attachments, {
        parentId: updatedInvoice.data.updateInvoice.id,
        parentType: ATTACHMENT_PARENT_TYPE.INVOICE,
      });

      toast.success(t("Invoice updated successfully"));
      return updatedInvoice.data.updateInvoice.id;
    } catch (error: any) {
      toast.error(t("Error updating invoice") + ": " + error.message);
      return "";
    }
  }

  async function handleUpdateInvoiceCustomFields(
    invoiceId: string,
    invoice: FormikInvoice,
  ): Promise<void> {
    try {
      await updateInvoice({
        variables: {
          id: invoiceId,
          input: {
            customFields: transformCustomFieldsToArray(invoice.customFields),
            customFieldsOnly: true,
          },
        },
      });

      toast.success(t("Custom fields updated successfully"));
    } catch (error: any) {
      toast.error(t("Error updating custom fields") + ": " + error.message);
      throw error;
    }
  }

  async function handleCancelInvoice(invoiceId: string): Promise<void> {
    try {
      await cancelInvoice({ variables: { id: invoiceId } });
      toast.success(t("Invoice canceled successfully"));
    } catch (error: any) {
      toast.error(t("Error canceling invoice") + ": " + error.message);
    }
  }

  async function handleUncancelInvoice(invoiceId: string): Promise<void> {
    try {
      await uncancelInvoice({ variables: { id: invoiceId } });
      toast.success(t("Invoice now active"));
    } catch (error: any) {
      toast.error(t("Error uncanceling invoice") + ": " + error.message);
    }
  }

  async function handleCheckInvoiceNumber(
    invoiceNumber: string,
    invoiceId?: string,
  ): Promise<boolean> {
    try {
      const response = await doesInvoiceNumberExist({
        variables: { id: invoiceId, invoiceNumber: invoiceNumber },
      });

      return response.data.doesInvoiceNumberExist;
    } catch (error: any) {
      toast.error(t("Error checking invoice") + ": " + error.message);
      return false;
    }
  }

  async function handleSendInvoice(
    invoiceId: string,
    asEstimate: boolean = false,
  ): Promise<void> {
    try {
      await sendInvoice({
        variables: { id: invoiceId, sendAsEstimate: asEstimate },
      });
      toast.success(t("Invoice sent successfully"));
    } catch (error: any) {
      toast.error(t("Error sending invoice") + ": " + error.message);
    }
  }

  async function handleResendFailedPaymentNotice(
    invoiceId: string,
  ): Promise<void> {
    try {
      await resendFailedPaymentNotice({ variables: { id: invoiceId } });
      toast.success(t("Payment failed notice re-sent to the customer"));
    } catch (error: any) {
      toast.error(
        t("Error re-sending payment failed notice") + ": " + error.message,
      );
    }
  }

  async function handleRefundInvoice({
    invoiceId,
    amount,
    reason,
    allowDuplicate = false,
  }: {
    invoiceId: string;
    amount: number;
    reason: string;
    allowDuplicate?: boolean;
  }): Promise<void> {
    await refundInvoice({
      variables: {
        id: invoiceId,
        input: {
          amount,
          // Stripe has no "other" reason: omitting the field is how you say it.
          ...(reason !== "other" && { reason }),
          ...(allowDuplicate && { allowDuplicate }),
        },
      },
    });
  }

  // handlers for line items

  const handleRemoveAsset = (assetId: string) => {
    formikInvoice.setFieldValue(
      "attachments",
      formikInvoice.values.attachments.filter(
        (attachment) => attachment.id !== assetId,
      ),
    );
  };

  const handleOpenEditInvoicePage = (invoiceId: string) => {
    navigate(`/admin/invoices/send-an-invoice?id=${invoiceId}`);
  };

  const handleOpenInvoiceHistoryPage = (invoiceId?: string) => {
    if (!invoiceId) navigate(`/admin/invoices`);
    else navigate(`/admin/invoices?id=${invoiceId}`);
  };

  const specificLoadingStates = {
    creatingInvoice,
    populatingInvoice,
    updatingInvoice,
    updatingInvoiceDetail,
    cancelingInvoice,
    uncancelingInvoice,
    sendingInvoice,
    resendingFailedNotice,
    loadingInvoiceNumber,
    refundingInvoice,
  };

  const isAnyLoading = Object.values(specificLoadingStates).some(
    (loading) => loading,
  );

  return {
    formikInvoice,
    customFieldSettings,
    specificLoadingStates,
    isAnyLoading,
    invoiceData: invoiceData?.invoice,
    getMostRecentInvoiceNumber,
    handleCancelInvoice,
    handleUncancelInvoice,
    handleCheckInvoiceNumber,
    handleSaveInvoice,
    handleSendInvoice,
    handleResendFailedPaymentNotice,
    handleRemoveAsset,
    handleOpenEditInvoicePage,
    handleOpenInvoiceHistoryPage,
    refetchInvoice,
    handleRefundInvoice,
  };
}
